import { useCallback } from "react";

import { useToast } from "@hightouch/htui";
import { AgentType, CreationSource } from "@hightouch/lib/llm/shared";
import { useNavigate } from "src/router";
import { type MetadataInput, useCreateAgentSessionMutation } from "src/graphql";

/**
 * Each call opens a fresh routing session seeded with `prompt` (not a follow-up
 * in the current conversation) and navigates to it. `metadata` records where the
 * session was started from (e.g. an insight card).
 */
export function useStartChatFromPrompt(): (
  prompt: string,
  metadata?: MetadataInput,
) => Promise<void> {
  const createChatSession = useCreateAgentSessionMutation();
  const navigate = useNavigate();
  const { toast } = useToast();

  return useCallback(
    async (prompt: string, metadata?: MetadataInput) => {
      try {
        const res = await createChatSession.mutateAsync({
          input: {
            initialUserPrompt: prompt,
            context: {
              type: AgentType.Routing,
              context: { flags: { fastMode: true } },
            },
            creationSource: CreationSource.Agents,
            metadata,
          },
        });

        const sessionId = res.createAgentSession?.sessionId;
        if (sessionId) {
          navigate(`/agents/chats/${sessionId}`);
        }
      } catch (error) {
        toast({
          id: "card-action-start-chat-error",
          title: "Could not start a chat",
          message: error instanceof Error ? error.message : "Please try again.",
          variant: "error",
        });
      }
    },
    [createChatSession, navigate, toast],
  );
}
